/**
 * Arrastar uma imagem de um plugin web para o chat (docs/spec/web-plugins.md, "Arrastar para o
 * chat").
 *
 * O iframe do plugin é de outra origem, e o arrastar nativo do navegador não passa dele para cá: a
 * página manda a posição do ponteiro (que fica preso a ela até soltar), uma miniatura segue o ponteiro
 * aqui, e o alvo registrado debaixo dele (o chat) recebe o arquivo ao soltar.
 */
import { api } from '../../api.js';
import { useApp } from '../../store.js';

export type PluginDropFile = { id: string; name: string; mime: string; url: string };
export type PluginDropTarget = { hover(on: boolean): void; drop(file: PluginDropFile): void };

const targets = new Map<Element, PluginDropTarget>();

/** Registra um lugar que aceita a imagem (o chat); devolve a função que tira o registro. */
export function registerPluginDrop(el: Element, target: PluginDropTarget): () => void {
  targets.set(el, target);
  return () => {
    if (targets.get(el) === target) targets.delete(el);
  };
}

/** O alvo debaixo do ponto (o mais de dentro, se um alvo estiver dentro de outro). */
function targetAt(doc: Document, x: number, y: number): PluginDropTarget | null {
  for (let el: Element | null = doc.elementFromPoint(x, y); el; el = el.parentElement) {
    const target = targets.get(el);
    if (target) return target;
  }
  return null;
}

/**
 * Um arrastar, do `drag.start` ao `drag.end` (ou `drag.cancel`). Pergunta o arquivo ao servidor, que
 * também confere que a pessoa o abre; sem resposta, solta em nada.
 */
export class PluginDrag {
  private file: PluginDropFile | null = null;
  private ghost: HTMLElement | null = null;
  private over: PluginDropTarget | null = null;
  private point: { x: number; y: number };
  private done = false;

  constructor(
    private readonly iframe: HTMLIFrameElement,
    fileId: string,
    x: number,
    y: number,
  ) {
    this.point = this.toPage(x, y);
    const token = useApp.getState().token;
    if (!token) {
      this.done = true;
      return;
    }
    api
      .fileUrl(token, fileId)
      .then((info) => {
        if (this.done) return;
        this.file = { id: fileId, name: info.name, mime: info.mime, url: info.url };
        this.showGhost();
        this.hitTest();
      })
      .catch(() => this.cancel());
  }

  /** Coordenadas da página do plugin → coordenadas desta página. */
  private toPage(x: number, y: number): { x: number; y: number } {
    const r = this.iframe.getBoundingClientRect();
    return { x: r.left + this.iframe.clientLeft + x, y: r.top + this.iframe.clientTop + y };
  }

  move(x: number, y: number): void {
    if (this.done) return;
    this.point = this.toPage(x, y);
    this.place();
    this.hitTest();
  }

  end(x: number, y: number): void {
    if (this.done) return;
    this.move(x, y);
    const target = this.file ? this.over : null;
    const file = this.file;
    this.finish();
    if (target && file) target.drop(file);
  }

  cancel(): void {
    if (!this.done) this.finish();
  }

  private hitTest(): void {
    // até o servidor responder, não acende nada: soltar antes disso cai em nada
    const next = this.file ? targetAt(this.iframe.ownerDocument, this.point.x, this.point.y) : null;
    if (next === this.over) return;
    this.over?.hover(false);
    this.over = next;
    this.over?.hover(true);
  }

  private finish(): void {
    this.done = true;
    this.over?.hover(false);
    this.over = null;
    this.ghost?.remove();
    this.ghost = null;
  }

  private showGhost(): void {
    const doc = this.iframe.ownerDocument;
    const file = this.file!;
    const isImage = file.mime.startsWith('image/');
    const ghost = doc.createElement(isImage ? 'img' : 'div');
    ghost.className = 'rd-pluginDrag';
    if (ghost instanceof HTMLImageElement) {
      ghost.src = file.url;
      ghost.alt = '';
    } else {
      ghost.textContent = file.name;
    }
    doc.body.appendChild(ghost);
    this.ghost = ghost;
    this.place();
  }

  private place(): void {
    if (this.ghost) this.ghost.style.transform = `translate(${Math.round(this.point.x)}px, ${Math.round(this.point.y)}px) translate(-50%, -50%)`;
  }
}
